import { useConvertToDevboxModal } from "components/GlobalModal/useConvertToDevboxModal";
import { useEditor } from "features/editor/Feature";
import type { VisibilityNumber } from "features/editor/sandboxUtils";
import { useSession } from "hooks/api/useSession";
import {
  Avatar,
  Dropdown,
  Stack,
  Box,
  Badge,
  DropdownButton,
  Icon,
} from "prism-react";
import type { Dispatch } from "react";

import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useAnalytics } from "hooks/useAnalytics";
import { useUserWorkspaces } from "hooks/useUserWorkspaces";
import { getIsPro } from "utils/subscription";

import type { PublicAction, State } from "../../features/forkSandbox";

export interface ForkSandboxButtonProps {
  fork: State;
  dispatchFork: Dispatch<PublicAction>;
  longText?: boolean;
}

export const ForkSandboxButton: React.FC<ForkSandboxButtonProps> = ({
  fork,
  dispatchFork,
  longText,
}) => {
  const activeWorkspace = useActiveWorkspace();
  const editor = useEditor();
  const { workspaces } = useUserWorkspaces();
  const [sessionInfo, dispatchSession] = useSession();
  const track = useAnalytics();

  const convertModal = useConvertToDevboxModal();

  if (editor.type !== "sandbox") {
    return;
  }

  const canConvertSandbox =
    editor.isCloud === false &&
    editor.authorization === "owner" &&
    activeWorkspace &&
    editor.isFrozen === false;

  const projectLabel = editor.isCloud ? "Devbox" : "Sandbox";

  const tooltip = (() => {
    if (sessionInfo.state === "UNAUTHENTICATED") {
      return `Sign in to fork this ${projectLabel}`;
    }

    return (
      `Fork this ${projectLabel}` +
      (activeWorkspace ? ` in ${activeWorkspace.name}` : "")
    );
  })();

  const forkText = longText ? `Fork ${projectLabel}` : "Fork";

  return (
    <DropdownButton
      buttonContent={
        sessionInfo.state === "UNAUTHENTICATED" ? "Sign In" : forkText
      }
      buttonTooltip={tooltip}
      loading={fork.state === "FORKING"}
      onClick={() => {
        if (sessionInfo.state === "UNAUTHENTICATED") {
          dispatchSession.signIn();
        } else {
          const isOwnedByUs =
            editor.type === "sandbox" &&
            editor.workspace?.id &&
            activeWorkspace?.id &&
            editor.workspace.id === activeWorkspace.id;

          if (isOwnedByUs) {
            const label = editor.isCloud ? "Devbox" : "Sandbox";
            const confirmation = window.confirm(
              `You're going to fork a ${label} that you already own, do you want to continue?`,
            );
            if (!confirmation) {
              return;
            }
          }

          let privacy: VisibilityNumber = editor.privacy;
          let collectionId = editor.collection?.id;

          if (editor.draft) {
            privacy = 2;
            collectionId = undefined;
          }

          dispatchFork({
            type: "CREATE_FORK",
            workspaceId: activeWorkspace?.id,
            privacy,
            collectionId,
          });
        }
      }}
      variant="secondary"
    >
      <Dropdown.Item
        disabled={!canConvertSandbox}
        onClick={() => {
          convertModal.open();
          track("convert_to_devbox_open", { location: "primary-button" });
        }}
      >
        <Stack gap={2} horizontal>
          <Icon size={4} type="server" />
          <span>Fork as Devbox</span>
        </Stack>
      </Dropdown.Item>

      <Dropdown.Separator />

      {workspaces?.map((ws) => (
        <Dropdown.Item
          key={ws.id}
          onClick={() => {
            let privacy: VisibilityNumber = editor.privacy;
            let collectionId = editor.collection?.id;

            if (editor.draft) {
              privacy = 2;
              collectionId = undefined;
            }

            dispatchFork({
              type: "CREATE_FORK",
              workspaceId: ws.id,
              privacy,
              collectionId,
            });
          }}
        >
          <Stack css={{ width: "100%" }} horizontal>
            <Avatar name={ws.name} shape="block" size="xs" src={ws.avatarUrl} />
            <span>{ws.name}</span>
            <Box css={{ marginLeft: "auto" }}>
              {getIsPro(ws) ? <Badge variant="accentPrimary">Pro</Badge> : null}
            </Box>
          </Stack>
        </Dropdown.Item>
      ))}
    </DropdownButton>
  );
};
